import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { RoundImage } from '../Image/RoundImage';
import { RepPositions } from '../../utils/enums';

export const RepsWrapper = styled.div`
    display: flex;
    position: relative;
    flex: 1;
    ${props => !props.isPortrait && `justify-content: space-evenly`};
    flex-direction: ${props => (props.isPortrait ? `column` : `row`)};
    margin-top: ${props =>
        props.isPortrait ? 1 : props.nextActionVisible ? 0.5 : 0.5}em;
    align-items: ${props =>
        props.nextActionVisible || props.isPortrait ? `flex-start` : `center`};
    ${props =>
        props.nextActionVisible &&
        `height: calc(100% - ${props.isPortrait ? 145 : 105}px)`};
    ${props =>
        props.repStanceLoading && `margin-bottom: ${props.isPortrait ? 40 : 70}px;`};
    @media (min-width: 24em) {
        margin-top: 0.5em;
    }
`;

const RepPositionContainer = styled.div`
    display: flex;
    flex-direction: ${props => (props.isPortrait ? `row` : `column`)};
    align-items: center;
    ${props => !props.isPortrait && `max-width: 33%;`};
    ${props => props.isPortrait && `margin: 10px 0;`};

    p {
        font-family: Averta;
        font-size: ${props => (props.nextActionVisible ? 13 : 16)}px;
        font-weight: bold;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.25;
        letter-spacing: normal;
        color: #000000;
        margin: ${props => (props.nextActionVisible ? `5px 0 2.5px` : `10px 0`)};
    }
`;

const RepDetails = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${props => props.isPortrait && `20px`};
    max-width: ${props => props.isPortrait && `75%`};
    justify-items: ${props => (props.isPortrait ? `flex-start` : `center`)};
    text-align: ${props => (props.isPortrait ? `left` : `center`)};
    flex: 1;

    p {
        font-size: ${props => (props.nextActionVisible ? 15 : 18)}px;
    }
`;

const RepPortraitContainer = styled.div`
    position: relative;
`;

const LearnMore = styled.button`
    background-color: white;
    height: 32px;
    max-width: 100px;
    border-radius: 4px;
    border: solid 1px #f1f1ee;
    font-family: Averta;
    font-size: 10px;
    font-weight: bold;
    line-height: 2.4;
    color: #000000;
    cursor: pointer;
    padding: 0 1em;
    ${props => !props.isPortrait && `margin: 0 auto`};

    &:hover {
        background-color: ${({ theme }) => theme.colors.primary};
        border: ${({ theme }) => `solid 1px ${theme.colors.primary}`};
        color: #ffffff;
    }

    &:active,
    &:focus {
        outline: none;
    }
`;

const IconWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ theme }) => theme.colors.secondary};
    height: ${props => (props.nextActionVisible ? 26 : 30)}px;
    border-radius: ${props => (props.nextActionVisible ? 13 : 15)}px;
    border: 2px solid white;
    width: max-content;
    margin: 0 auto;
    margin-top: ${props => (props.nextActionVisible ? -13 : -15)}px;

    span {
        color: white;
        font-size: ${props => (props.nextActionVisible ? 10 : 11)}px;
        font-family: Averta;
        font-weight: bold;
        text-transform: uppercase;
        ${props => (props.iconVisible ? `padding-right: 1em;` : `padding: 0 1em`)};
    }

    ${props =>
        props.isPortrait &&
        `
            position: absolute;
            width: 28px;
            height: 28px;
            border-radius: 14px;
            right: 0;
            bottom: 0;

            div {
                position: absolute;
                top: -7px;
            }
        `};
`;

const Icon = styled.div`
    margin-top: 5px;
    ${props => props.isIconVisible && `margin-right: -0.5em;`};
`;

const YesIcon = props => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={props.size || 28}
        height={props.size || 28}
        viewBox="0 0 28 28"
    >
        <g fill="none" fillRule="evenodd">
            <path
                fill="#FFF"
                d="M12.992 19.913L8.139 15.06l2.121-2.122 2.349 2.349 4.97-6.959 2.441 1.742z"
            />
        </g>
    </svg>
);
YesIcon.propTypes = { size: PropTypes.number };

const NoIcon = props => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={props.size || 28}
        height={props.size || 28}
        viewBox="0 0 28 28"
    >
        <g fill="none" fillRule="evenodd">
            <path
                fill="#FFF"
                d="M19.648 10.048L17.952 8.35 14 12.303 10.048 8.35l-1.696 1.697 3.951 3.951-3.951 3.952 1.696 1.697L14 15.698l3.952 3.95 1.696-1.697-3.951-3.952z"
            />
        </g>
    </svg>
);
NoIcon.propTypes = { size: PropTypes.number };

const NeutralIcon = props => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={props.size || 28}
        height={props.size || 28}
        viewBox="0 0 28 28"
    >
        <g fill="none" fillRule="evenodd">
            <path
                stroke="#FFF"
                strokeLinecap="square"
                strokeWidth="3"
                d="M8.429 13.793h10.728"
            />
        </g>
    </svg>
);
NeutralIcon.propTypes = { size: PropTypes.number };

const StanceIcon = props => (
    <IconWrapper
        theme={props.theme}
        isPortrait={props.isPortrait}
        nextActionVisible={props.nextActionVisible}
        iconVisible={isStandardAnswer(props.answer)}
    >
        <Icon iconVisible={isStandardAnswer(props.answer)}>
            {props.answer === RepPositions.yes && <YesIcon />}
            {props.answer === RepPositions.no && <NoIcon />}
            {(props.answer === RepPositions.unknown ||
                props.answer === RepPositions.unavailable) && <NeutralIcon />}
        </Icon>
        {!props.isPortrait && (
            <span>
                {props.answer === RepPositions.unknown ||
                props.answer === RepPositions.unavailable
                    ? 'Unknown'
                    : props.answer}
            </span>
        )}
    </IconWrapper>
);

StanceIcon.propTypes = {
    answer: PropTypes.string,
    nextActionVisible: PropTypes.bool,
    isPortrait: PropTypes.bool,
    theme: PropTypes.object
};

const RepPortrait = props => (
    <RepPortraitContainer>
        <RoundImage
            size={props.isPortrait || props.nextActionVisible ? 75 : 100}
            src={props.src}
        />
        {(isStandardAnswer(props.answer) || !props.isPortrait) && ( //eslint-disable-line
            <StanceIcon
                theme={props.theme}
                isPortrait={props.isPortrait}
                nextActionVisible={props.nextActionVisible}
                answer={props.answer}
            />
        )}
    </RepPortraitContainer>
);

RepPortrait.propTypes = {
    answer: PropTypes.string,
    isPortrait: PropTypes.bool.isRequired,
    nextActionVisible: PropTypes.bool.isRequired,
    src: PropTypes.string.isRequired,
    theme: PropTypes.object.isRequired
};

const isStandardAnswer = answer =>
    answer === RepPositions.yes ||
    answer === RepPositions.no ||
    answer === RepPositions.unknown ||
    answer === RepPositions.unavailable;

export const RepPosition = props => (
    <RepPositionContainer
        nextActionVisible={props.nextActionVisible}
        isPortrait={props.isPortrait}
    >
        <RepPortrait
            theme={props.theme}
            nextActionVisible={props.nextActionVisible}
            isPortrait={props.isPortrait}
            answer={props.answer}
            src={props.src}
        />
        <RepDetails
            nextActionVisible={props.nextActionVisible}
            answer={props.answer}
            isPortrait={props.isPortrait}
            className="ie-rep-stance-button-container"
        >
            <p>
                {props.isPortrait
                    ? `${props.repName}: ${
                          props.answer === RepPositions.unknown ||
                          props.answer === RepPositions.unavailable
                              ? 'Unknown'
                              : props.answer
                      }`
                    : props.repName}
            </p>
            <LearnMore
                theme={props.theme}
                isPortrait={props.isPortrait}
                onClick={props.handleClick}
            >
                {props.label || 'LEARN MORE'}
            </LearnMore>
        </RepDetails>
    </RepPositionContainer>
);

RepPosition.propTypes = {
    answer: PropTypes.string,
    handleClick: PropTypes.func.isRequired,
    isPortrait: PropTypes.bool.isRequired,
    label: PropTypes.string,
    nextActionVisible: PropTypes.bool.isRequired,
    repName: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
    theme: PropTypes.object.isRequired
};
